import { motion } from "motion/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ShoppingCart, Scan, LayoutGrid, List as ListIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { BarcodeInputHandler } from "../../features/pos/barcode/BarcodeInputHandler";
import { BarcodeScanner } from "../../features/pos/barcode/BarcodeScanner";
import { QuickAddProductModal } from "../../features/pos/barcode/QuickAddProductModal";
import { TRACKING_EVENTS } from "../../constants/trackingEvents";
import { trackEvent } from "../../lib/tracking";
import { queryClient } from "../queryClient";
import { PosBillSummary } from "../../features/pos/components/PosBillSummary";
import { PosCartItemCard } from "../../features/pos/components/PosCartItemCard";
import { formatInrTrimmed } from "../../lib/currency";
import { usePosSession } from "../../features/pos/hooks/usePosSession";

export function POSPage() {
  const s = usePosSession();

  return (
    <div className="p-6 h-full min-h-0">
      {s.resolving ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <div className="rounded-lg border bg-card px-6 py-4 shadow-lg text-sm text-muted-foreground">
            Fetching product details...
          </div>
        </div>
      ) : null}

      <QuickAddProductModal
        open={s.scanModalOpen}
        onOpenChange={s.setScanModalOpen}
        barcode={s.scanBarcode}
        mode={s.scanModalMode}
        externalPrefill={s.scanExternalPrefill}
        brands={s.brands}
        categories={s.categories}
        dealers={s.dealers}
        onSuccess={async ({ id, name }) => {
          queryClient.removeQueries({ queryKey: ["posPricing", id] });
          await s.reloadProducts();
          const ok = await s.addProductToCart(id, name);
          if (ok) toast.success("Product added");
        }}
      />

      <BarcodeInputHandler
        enabled={s.scannerMode === "usb"}
        onBarcode={(b) => {
          trackEvent(TRACKING_EVENTS.BARCODE_SCANNED, { type: "usb" });
          void s.resolveBarcode(b);
        }}
      />
      <BarcodeScanner
        open={s.cameraOpen}
        onClose={() => s.setCameraOpen(false)}
        onDetected={(b) => {
          trackEvent(TRACKING_EVENTS.BARCODE_SCANNED, { type: "camera" });
          void s.resolveBarcode(b);
        }}
      />
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-foreground mb-1">Point of Sale</h1>
        <p className="text-muted-foreground">Process customer transactions</p>
      </div>

      <div className="lg:pr-[min(26rem,calc(100vw-2rem))]">
        <div>
          <Card className="border-0 shadow-sm flex flex-col min-h-0 max-h-[calc(100vh-12rem)] lg:max-h-[calc(100vh-8rem)]">
            <CardHeader className="shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Scan barcode or search products..."
                    className="pl-11 bg-input-background border-0"
                    value={s.query}
                    onChange={(e) => s.setQuery(e.target.value)}
                  />
                </div>

                {s.scannerMode === "camera" ? (
                  <Button type="button" className="shrink-0" onClick={() => s.setCameraOpen(true)}>
                    <Scan className="w-4 h-4 mr-2" />
                    Scan
                  </Button>
                ) : null}

                <div className="shrink-0 flex items-center gap-1 rounded-md border bg-background p-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={s.productView === "grid" ? "default" : "ghost"}
                    className="h-8 px-2"
                    onClick={() => s.setProductView("grid")}
                    aria-pressed={s.productView === "grid"}
                    title="Grid view"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={s.productView === "list" ? "default" : "ghost"}
                    className="h-8 px-2"
                    onClick={() => s.setProductView("list")}
                    aria-pressed={s.productView === "list"}
                    title="List view"
                  >
                    <ListIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-y-auto">
              {s.productsError ? (
                <div className="text-sm text-destructive">{s.productsError}</div>
              ) : s.productsLoading ? (
                <div className="text-sm text-muted-foreground">Loading products…</div>
              ) : s.filteredProducts.length === 0 ? (
                <div className="text-sm text-muted-foreground">No products found</div>
              ) : s.productView === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {s.filteredProducts.map((p) => {
                    const hasStock = Boolean(p.pos_active_batch);
                    const pid = String(p.id);
                    const pr = s.cardPricingByProductId[pid];
                    const outOfStock = !hasStock || (pr ? pr.available_qty <= 0 : false);
                    return (
                      <motion.div
                        key={p.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={[
                          "border rounded-lg p-4 transition-colors",
                          outOfStock ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-primary",
                        ].join(" ")}
                        onClick={() => {
                          if (outOfStock) return;
                          void s.addProductToCart(pid, p.name);
                        }}
                        tabIndex={0}
                      >
                        <div className="aspect-square bg-muted rounded-lg mb-3"></div>
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm mb-1">{p.name}</h4>
                          {outOfStock ? (
                            <Badge variant="destructive" className="shrink-0">
                              Out of stock
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-muted-foreground text-xs">{p.sku ? `SKU: ${p.sku}` : " "}</p>

                        {!pr ? null : pr.available_qty <= 0 ? (
                          <div className="mt-3">
                            <Badge variant="destructive">Out of stock</Badge>
                          </div>
                        ) : (
                          <div className="mt-3">
                            <div className="flex items-baseline gap-2">
                              <span className="font-semibold text-sm text-foreground">
                                {formatInrTrimmed(pr.unit_price)}
                              </span>
                              {pr.mrp !== null ? (
                                <span className="text-xs text-muted-foreground line-through">
                                  {formatInrTrimmed(pr.mrp)}
                                </span>
                              ) : null}
                            </div>
                            <Badge variant="secondary" className="mt-2">
                              Left {s.formatQty(pr.available_qty)}
                            </Badge>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="divide-y rounded-lg border overflow-hidden">
                  {s.filteredProducts.map((p) => {
                    const hasStock = Boolean(p.pos_active_batch);
                    const pid = String(p.id);
                    const pr = s.cardPricingByProductId[pid];
                    const outOfStock = !hasStock || (pr ? pr.available_qty <= 0 : false);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={[
                          "w-full text-left p-3 transition-colors",
                          "flex items-center justify-between gap-3",
                          outOfStock
                            ? "opacity-60 cursor-not-allowed"
                            : "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        ].join(" ")}
                        onClick={() => {
                          if (outOfStock) return;
                          void s.addProductToCart(pid, p.name);
                        }}
                        disabled={outOfStock}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">{p.name}</div>
                              <div className="text-muted-foreground text-xs truncate">
                                {p.sku ? `SKU: ${p.sku}` : " "}
                              </div>
                            </div>
                            {outOfStock ? (
                              <Badge variant="destructive" className="shrink-0">
                                Out of stock
                              </Badge>
                            ) : null}
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          {pr ? (
                            <div className="flex flex-col items-end">
                              <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-sm text-foreground">
                                  {formatInrTrimmed(pr.unit_price)}
                                </span>
                                {pr.mrp !== null ? (
                                  <span className="text-xs text-muted-foreground line-through">
                                    {formatInrTrimmed(pr.mrp)}
                                  </span>
                                ) : null}
                              </div>
                              {Number.isFinite(pr.available_qty) ? (
                                <span className="text-xs text-muted-foreground">
                                  Left {s.formatQty(pr.available_qty)}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">Loading…</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-6 lg:mt-0 lg:fixed lg:top-20 lg:right-6 lg:z-30 w-full lg:w-[min(24rem,calc(100vw-2rem))] lg:max-h-[calc(100vh-5.5rem)] flex flex-col min-h-0">
        <Card className="border-0 shadow-sm lg:shadow-lg flex flex-col flex-1 min-h-0 max-h-full overflow-hidden">
          <CardHeader className="shrink-0 pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="w-5 h-5" />
              Current Sale
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 min-h-0 gap-3 pt-0">
            <div className="space-y-3 shrink-0">
              <div className="space-y-1.5">
                <Label htmlFor="pos-customer-phone">Mobile</Label>
                <Input
                  id="pos-customer-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="10-digit mobile"
                  value={s.customerPhone}
                  onChange={(e) => s.setCustomerPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pos-customer-name">Customer name</Label>
                <Input
                  id="pos-customer-name"
                  type="text"
                  placeholder="Name"
                  value={s.customerName}
                  onChange={(e) => s.setCustomerName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Payment</Label>
                <Select
                  value={s.paymentMethod}
                  onValueChange={(v) => {
                    const m = v as "cash" | "razorpay" | "gpay" | "upi";
                    s.onPaymentMethodChange(m);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                    <SelectItem value="gpay">GPay</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pos-promo-code">Promo code</Label>
                <Input
                  id="pos-promo-code"
                  type="text"
                  placeholder="Optional"
                  value={s.promoCode}
                  onChange={(e) => s.setPromoCode(e.target.value)}
                />
              </div>
            </div>

            <Separator />

            <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
              {s.cart.length === 0 ? (
                <div className="text-sm text-muted-foreground">No items in cart</div>
              ) : (
                s.cartLines.map(({ item, gst }) => (
                  <PosCartItemCard
                    key={item.product_id}
                    item={item}
                    gst={gst}
                    expanded={s.expandedCartPid === item.product_id}
                    onExpandedChange={(open) => {
                      s.setExpandedCartPid((cur) => {
                        if (open) return item.product_id;
                        return cur === item.product_id ? null : cur;
                      });
                    }}
                    onQtyDelta={(q) => s.setItemQty(item.product_id, q)}
                    onChangeOverride={(patch) => {
                      s.setCart((prev) => prev.map((ci) => (ci.product_id === item.product_id ? { ...ci, ...patch } : ci)));
                    }}
                  />
                ))
              )}
            </div>

            <Separator />

            <PosBillSummary
              subtotalTaxable={s.quote ? Number.parseFloat(s.quote.totals.taxable) : s.totals.taxable}
              discountTotal={s.quote ? Number.parseFloat(s.quote.totals.discount) : 0}
              gstTotal={s.quote ? Number.parseFloat(s.quote.totals.gst) : s.totals.gst}
              grandTotal={s.quote ? Number.parseFloat(s.quote.totals.grand_total) : s.totals.grand}
              showComplianceWarning={s.showComplianceWarning}
              appliedPromotionLabel={
                s.quote?.applied_promotion
                  ? `${s.quote.applied_promotion.name}${s.quote.applied_promotion.code ? ` (${s.quote.applied_promotion.code})` : ""}`
                  : null
              }
            />

            <Button size="lg" className="w-full shrink-0" onClick={() => void s.onCheckout()} disabled={s.checkoutLoading}>
              {s.checkoutLoading ? "Completing…" : "Complete Sale"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
